import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  InputGroup,
  Badge,
  Dropdown,
  Spinner,
  Tabs,
  Tab
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faSearch,
  faSort,
  faDownload,
  faEye,
  faEdit,
  faEllipsisV,
  faSync,
  faHistory,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons'
import { Link, useParams, useHistory } from 'react-router-dom'
import { DebounceInput } from 'react-debounce-input'
import { connect } from 'react-redux'
import { withNamespaces } from 'react-i18next'
import Pagination from '../../../../GlobalModules/Utility/Components/Pagination'
import Utility from '../../../../GlobalModules/Utility/Utility'
import * as RequisitionController from '../Controller/RequisitionController'
import ProductsTab from '../Components/ProductsTab'
import RFQSendHistoryModal from '../Components/RFQSendHistoryModal'
import '../Assets/css/requisition.scss'

const RequisitionList = (props) => {
  const { lng } = useParams()
  const history = useHistory()

  const [rows, setRows] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [sortField, setSortField] = useState('id')
  const [sortDirection, setSortDirection] = useState('desc')
  const [loading, setLoading] = useState(true)
  const [totalItems, setTotalItems] = useState(0)
  const [selectedRange, setSelectedRange] = useState('all')
  const [requisitionsRange, setRequisitionsRange] = useState('all')
  const [productsRange, setProductsRange] = useState('all')
  const [selectedPriority, setSelectedPriority] = useState('all')
  const [activeTab, setActiveTab] = useState('requisitions')
  const [filters, setFilters] = useState({
    limit: 10,
    offset: 0,
    sort: [
      {
        colId: 'id',
        sort: 'desc'
      }
    ],
    search: '',
    from_created_date: '',
    to_created_date: '',
    priority: ''
  })
  const suppressNextFetchRef = useRef(false)
  const [showRFQHistory, setShowRFQHistory] = useState(false)
  const [selectedRequisitionId, setSelectedRequisitionId] = useState(null)
  
  const computeDateRange = (type) => {
    const today = new Date()
    const fmt = (d) => d.toISOString().split('T')[0]
    switch (type) {
      case 'today': return { from: fmt(today), to: fmt(today) }
      case 'next7': { const f = new Date(today); f.setDate(f.getDate() + 7); return { from: fmt(today), to: fmt(f) } }
      case 'nextMonth': { const f = new Date(today); f.setDate(f.getDate() + 30); return { from: fmt(today), to: fmt(f) } }
      default: return { from: '', to: '' }
    }
  }

  const setQuickRange = (type) => {
    const { from, to } = computeDateRange(type)
    // Update filters — useEffect will trigger the fetch when filters changes
    setFilters((prev) => ({
      ...prev,
      from_created_date: from,
      to_created_date: to,
      offset: 0
    }))
    setRequisitionsRange(type)
    setSelectedRange(type)
  }

  const handleTabChange = (k) => {
    setActiveTab(k)
    const targetRange = k === 'requisitions' ? requisitionsRange : productsRange
    if (targetRange !== 'custom') {
      const { from, to } = computeDateRange(targetRange)
      setFilters((prev) => ({ ...prev, from_created_date: from, to_created_date: to, offset: 0 }))
    }
  }

  const handleLimitChange = (limit) => {
    const newLimit = parseInt(limit)
    setItemsPerPage(newLimit)
    setCurrentPage(1)
    setFilters((prev) => ({ ...prev, limit: newLimit, offset: 0 }))
  }

  const handleQuickRangeClick = (e) => {
    const range = e.currentTarget.getAttribute('data-range')
    if (range === 'custom') {
      const today = new Date()
      const fmt = (d) => d.toISOString().split('T')[0]
      setRequisitionsRange('custom')
      setSelectedRange('custom')
      setFilters((prev) => ({
        ...prev,
        from_created_date: fmt(today),
        to_created_date: prev.to_created_date || fmt(today),
        offset: 0
      }))
      return
    }
    if (range === 'today') return setQuickRange('today')
    if (range === 'next7') return setQuickRange('next7')
    if (range === 'nextMonth') return setQuickRange('nextMonth')
    if (range === 'all') return setQuickRange('all')
  }

  const handlePriorityChange = (priority) => {
    setSelectedPriority(priority)
    setFilters(prev => ({ 
      ...prev, 
      priority: priority === 'all' ? '' : priority,
      offset: 0 
    }))
    setCurrentPage(1)
  }

  const handleRefresh = () => {
    performFetch()
  }

  const performFetch = useCallback(
    async (effectiveFilters) => {
      try {
        setLoading(true)
        if (props.userEntryType?.user_type === 'staff') {
          if (localStorage.getItem('store_id') == null) {
            localStorage.setItem('store_id', props.userEntryType.branch_id)
          }
        }
        const response = await RequisitionController.getRequisitionsApi(
          effectiveFilters
        )
        if (response.success) {
          const r = response.data || []
          setRows(r)
          setTotalItems(response.total || r.length)
        } else {
          setRows([])
        }
      } catch (error) {
        console.error('Error fetching requisitions:', error)
        setRows([])
      } finally {
        setLoading(false)
      }
    },
    [props.userEntryType]
  )

  const fetchRows = useCallback(async () => {
    await performFetch(filters)
  }, [filters, performFetch])

  useEffect(() => {
    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false
      return
    }
    if (activeTab !== 'requisitions') return
    fetchRows()
  }, [fetchRows, activeTab])

  // Auto-fetch when custom dates are both selected and requisitions tab is active
  useEffect(() => {
    if (
      activeTab === 'requisitions' &&
      requisitionsRange === 'custom' &&
      filters.from_created_date &&
      filters.to_created_date
    ) {
      performFetch({ ...filters })
    }
  }, [activeTab, requisitionsRange, filters, performFetch])

  
  const getCreatedDisplay = (row) => {
    const value =
      row.createdAt || row.created_at || row.createdAt_label || row.created_on
    return value ? Utility.get12HourFormat(value) : 'N/A'
  }

  const getUpdatedDisplay = (row) => {
    const value =
      row.updatedAt || row.updated_at || row.updatedAt_label || row.updated_on
    return value ? Utility.get12HourFormat(value) : 'N/A'
  }

  const handleSearch = (e) => {
    const raw = e.target.value || ''
    const clean = raw.replace(/\s+/g, '')
    setSearchTerm(clean)
    setCurrentPage(1)
    const next = {
      ...filters,
      search: clean.length >= 2 ? clean : '',
      offset: 0
    }
    suppressNextFetchRef.current = true
    setFilters(next)
    if (clean.length >= 2 || clean.length === 0) {
      performFetch(next)
    }
  }

  // Removed Enter-key instant search to rely on debounced input

  const startIndex = totalItems === 0 ? 0 : filters.offset + 1
  const endIndex =
    totalItems === 0 ? 0 : Math.min(filters.offset + rows.length, totalItems)

  const handlePageChange = (page) => {
    setCurrentPage(page)
    setFilters((prev) => ({ ...prev, offset: (page - 1) * prev.limit }))
  }

  const handleSort = (field) => {
    let newSortDirection = 'asc'
    if (sortField === field) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      setSortField(field)
    }
    setSortDirection(newSortDirection)
    setFilters((prev) => ({
      ...prev,
      sort: [
        {
          colId: field,
          sort: newSortDirection
        }
      ]
    }))
  }

  const getStatusBadge = (status) => {
    // Normalize status which may be number, string, or nested object
    let raw = status
    if (raw && typeof raw === 'object') {
      if (raw.status_data) {
        raw =
          raw.status_data.value_constant ||
          raw.status_data.value ||
          raw.status_data.label ||
          raw.status
      } else {
        raw = raw.value_constant || raw.value || raw.status || raw.label
      }
    }
    const key = typeof raw === 'string' ? raw.toUpperCase() : raw
    const map = {
      // Numeric
      0: { label: 'URGENT', bg: 'danger' },
      1: { label: 'LOW', bg: 'primary' },
      2: { label: 'MEDIUM', bg: 'warning' },
      3: { label: 'HIGH', bg: 'danger' },
      4: { label: 'DRAFT', bg: 'secondary' },
      // Constants
      URGENT: { label: 'URGENT', bg: 'danger' },
      LOW: { label: 'LOW', bg: 'primary' },
      MEDIUM: { label: 'MEDIUM', bg: 'warning' },
      HIGH: { label: 'HIGH', bg: 'danger' },
      DRAFT: { label: 'DRAFT', bg: 'secondary' }
    }
    const info = map[key] || { label: 'Unknown', bg: 'light' }
    return <Badge bg={info.bg}>{info.label}</Badge>
  }

  //======= add action permission ====//
  // Check multiple permissions at once
  const requisitionViewPermissions = Utility.hasMultipleActionPermissions(
    'sales_and_orders',
    'requisitions',
    ['create', 'delete', 'export', 'change_status', 'transfer_product'],
    props.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  // Handle Transfer Product - fetch requisition details and redirect to stock transfer form
  const handleTransferProduct = async (requisitionSlack) => {
    try {
      const response = await RequisitionController.getRequisitionApi(requisitionSlack);
      if (response.success && response.data) {
        const requisitionData = response.data;

        // Transform requisition data to stock transfer format
        const stockTransferData = {
          from_store_id: requisitionData.ProcurementStore.id,
          to_store_id: requisitionData.RequestingStore.id,
          from_store_details: requisitionData.ProcurementStore,
          to_store_details: requisitionData.RequestingStore,
          notes: requisitionData.purpose ? `${requisitionData.requisition_number}: ${requisitionData.purpose}` : requisitionData.requisition_number,
          products: requisitionData.products.map((product) => ({
            slack: product.Product.slack,
            name: product.Product.name,
            quantity: parseFloat(product.quantity_requested),
            quantity_left: product.Product.quantity || product.quantity_requested
          })),
          stock_transfer_reference: requisitionData?.requisition_number,
          requisition_slack: requisitionData?.slack,
          requisition_id: requisitionData?.id
        };

        // Store data in localStorage to pass to stock transfer form
        localStorage.setItem('stockTransferFromRequisition', JSON.stringify(stockTransferData));

        // Redirect to stock transfer form
        history.push(`/${lng}/stock/transfer/add`);
      } else {
        Utility.toastNotifications('Failed to fetch requisition details', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error fetching requisition details:', error);
      Utility.toastNotifications('Error fetching requisition details', 'Error', 'error');
    }
  };

  return (
    <Container fluid className='p-0'>
      <div className='d-flex justify-content-between align-items-center top-header-box'>
        <h4>Requisitions</h4>
        <div>
          {requisitionViewPermissions.export && (
          <Button
            variant='outline-secondary'
            className='me-2'
            disabled={loading}
            onClick={useCallback(async () => {
              try {
                setLoading(true)
                const exportData = {
                  from_created_date: filters.from_created_date,
                  to_created_date: filters.to_created_date,
                  search: filters.search,
                  priority: filters.priority
                }
                const response = await RequisitionController.exportRequisitionsApi(
                  exportData
                )
                if (response && response.success && response.data) {
                  if (response.isFileUrl) {
                    await Utility.downloadFile(
                      response.data,
                      response.filename || 'requisitions_export.xlsx'
                    )
                  } else {
                    const blob = new Blob([response.data], {
                      type:
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    })
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = response.filename || 'requisitions_export.xlsx'
                    document.body.appendChild(a)
                    a.click()
                    window.URL.revokeObjectURL(url)
                    document.body.removeChild(a)
                  }
                }
              } catch (e) {
                console.error('Export requisitions failed:', e)
              } finally {
                setLoading(false)
              }
            }, [filters])}
          >
            <FontAwesomeIcon icon={faDownload} className='me-2' /> {loading ? 'Export...' : 'Export'}
          </Button>)}
          {requisitionViewPermissions.create && (
          <Button
            variant='primary'
            onClick={() => history.push(`/${lng}/sales/requisitions/add`)}
          >
            <FontAwesomeIcon icon={faPlus} className='me-2' /> New Requisition
          </Button>)}
        </div>
      </div>

      <Card className='custom-border-light'>
        <Card.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={handleTabChange}
            className="mb-3"
          >
            <Tab eventKey="requisitions" title="Requisitions">
              {/* Removed From/To date search as requested */}

              <Row className='mb-3 gx-2 searchBarPanel'>
            <Col className='d-flex align-items-center flex-wrap gap-2 innSearchBarPanel'>
              {selectedRange === 'custom' && (
                <div className='d-flex align-items-center gap-2'>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    placeholder='dd.mm.yyyy'
                    value={filters.from_created_date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        from_created_date: e.target.value,
                        offset: 0
                      }))
                    }
                    style={{ width: 160 }}
                  />
                  <span>to</span>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    placeholder='dd.mm.yyyy'
                    min={filters.from_created_date || new Date().toISOString().split('T')[0]}
                    value={filters.to_created_date}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        to_created_date: e.target.value,
                        offset: 0
                      }))
                    }
                    style={{ width: 160 }}
                  />
                </div>
              )}
              <div className='btn-group ms-auto'>
                <Button
                  size='sm'
                  variant={requisitionsRange === 'today' ? 'primary' : 'light'}
                  data-range='today'
                  onClick={handleQuickRangeClick}
                >
                  Today
                </Button>
                <Button
                  size='sm'
                  variant={requisitionsRange === 'next7' ? 'primary' : 'light'}
                  data-range='next7'
                  onClick={handleQuickRangeClick}
                >
                  Next 7 Days
                </Button>
                <Button
                  size='sm'
                  variant={requisitionsRange === 'nextMonth' ? 'primary' : 'light'}
                  data-range='nextMonth'
                  onClick={handleQuickRangeClick}
                >
                  Next Month
                </Button>
                <Button
                  size='sm'
                  variant={requisitionsRange === 'custom' ? 'primary' : 'light'}
                  data-range='custom'
                  onClick={handleQuickRangeClick}
                >
                  Custom Dates
                </Button>
                <Button
                  size='sm'
                  variant={requisitionsRange === 'all' ? 'primary' : 'light'}
                  data-range='all'
                  onClick={handleQuickRangeClick}
                >
                  All
                </Button>
              </div>
              <div className='d-flex align-items-center gap-2 ms-2'>
                <span>Priority:</span>
                <select
                  className='form-select form-select-sm'
                  style={{ width: 120 }}
                  value={selectedPriority}
                  onChange={(e) => handlePriorityChange(e.target.value)}
                >
                <option value='LOW'>LOW</option>
                <option value='MEDIUM'>MEDIUM</option>
                <option value='HIGH'>HIGH</option>
                <option value='URGENT'>URGENT</option>
                <option value='all'>ALL</option>
                </select>
              </div>
              {/* <div className='d-flex align-items-center gap-2 ms-2'>
                <span>Show</span>
                <select
                  className='form-select form-select-sm'
                  style={{ width: 80 }}
                  value={itemsPerPage}
                  onChange={(e) => handleLimitChange(e.target.value)}
                >
                  {[10, 25, 50, 100, 500, 1000].map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <span>entries</span>
              </div> */}
              <div className='searchFieldBoxes'>
                <div className='ms-2 searchFieldBox searchFieldBoxCustom'>
                  <InputGroup size='sm'>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroup.Text>
                    <DebounceInput
                      minLength={1}
                      debounceTimeout={500}
                      className='form-control'
                      placeholder='Search by Requisition number & Status'
                      value={searchTerm || ''}
                      onChange={(e) => handleSearch(e)}
                      onKeyDown={(e) => {
                        if (e.key === ' ') e.preventDefault()
                      }}
                    />
                  </InputGroup>
                </div>
                <div className='ms-2'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={handleRefresh}
                    disabled={loading}
                    title='Refresh'
                  >
                    <FontAwesomeIcon
                      icon={faSync}
                      spin={loading}
                    />
                  </Button>
                </div>
              </div>

            </Col>
          </Row>

          <div className='table-responsive'>
            <Table hover className='align-middle'>
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('requisition_number')}
                    style={{ cursor: 'pointer' }}
                  >
                    Requisition Number{' '}
                    {sortField === 'requisition_number' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    // onClick={() => handleSort('store_name')}
                    style={{ cursor: 'pointer' }}
                  >
                    Store Name{' '}
                    {/* {sortField === 'store_name' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )} */}
                  </th>
                  <th>
                    Items
                  </th>
                  {/* <th
                    onClick={() => handleSort('requisition_reference')}
                    style={{ cursor: 'pointer' }}
                  >
                    Requisition Reference #{' '}
                    {sortField === 'requisition_reference' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th> */}
                 
                  <th
                    onClick={() => handleSort('priority')}
                    style={{ cursor: 'pointer' }}
                  >
                    Priority{' '}
                    {sortField === 'priority' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  {/* <th
                    onClick={() => handleSort('purpose')}
                    style={{ cursor: 'pointer' }}
                  >
                    Purpose{' '}
                    {sortField === 'purpose' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th> */}
                   <th
                    onClick={() => handleSort('total_estimated_amount')}
                    style={{ cursor: 'pointer' }}
                  >
                    Estimated Amount{' '}
                    {sortField === 'total_estimated_amount' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('requisition_date')}
                    style={{ cursor: 'pointer' }}
                  >
                    Requisition Date{' '}
                    {sortField === 'requisition_date' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('required_by_date')}
                    style={{ cursor: 'pointer' }}
                  >
                    Delivery Due Date{' '}
                    {sortField === 'required_by_date' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  {/* <th
                    onClick={() => handleSort('total_order_amount')}
                    style={{ cursor: 'pointer' }}
                  >
                    Amount{' '}
                    {sortField === 'total_order_amount' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th> */}
                  <th
                    onClick={() => handleSort('status')}
                    style={{ cursor: 'pointer' }}
                  >
                    Status{' '}
                    {sortField === 'status' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('createdAt')}
                    style={{ cursor: 'pointer' }}
                  >
                    Created On{' '}
                    {sortField === 'createdAt' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  {/* <th
                    onClick={() => handleSort('updated_at')}
                    style={{ cursor: 'pointer' }}
                  >
                    Updated On{' '}
                    {sortField === 'updated_at' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th> */}
                  <th
                    // onClick={() => handleSort('created_at')}
                    style={{ cursor: 'pointer' }}
                  >
                    Requested By{' '}
                    {/* {sortField === 'created_at' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )} */}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan='12' className='text-center py-4'>
                      {/* <Spinner animation='border' variant='primary' />
                      <span className='ms-2'>Loading requisitions...</span> */}
                    </td>
                  </tr>
                ) : rows.length > 0 ? (
                  rows.map((q) => (
                    <tr 
                      key={q.id}
                      onDoubleClick={() => history.push(`/${lng}/sales/requisitions/view/${q.slack}`)}
                      className='cursor-pointer'
                    >
                      <td>{q.requisition_number}</td>
                      <td>{q.store_name}</td>
                      <td>{q?.products_count}</td>
                      {/* <td>{q.requisition_reference || 'N/A'}</td> */}
                      
                      <td>{getStatusBadge(q.priority)}</td>
                      {/* <td>{q.purpose}</td> */}
                      <td>{Utility.formatCurrencyValue(q.total_estimated_amount)}</td>
                      <td>{q.requisition_date}</td>
                      <td>{q.requisition_due_date}</td>
                      {/* <td>
                        {parseFloat(q.total_order_amount || 0).toFixed(2)}
                      </td> */}
                      <td className="status-cell">{Utility.getStatusDataBadge(q.status_data)}</td>
                      <td>{getCreatedDisplay(q)}</td>
                      {/* <td>{getUpdatedDisplay(q)}</td> */}
                      <td>{q.created_by}</td>
                      <td>
                        <Dropdown align='end'>
                          <Dropdown.Toggle
                            variant='light'
                            size='sm'
                            id={`dropdown-${q.id}`}
                            className='btn-icon no-caret'
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              as={Link}
                              to={`/${lng}/sales/requisitions/view/${q.slack}`}
                            >
                              <FontAwesomeIcon icon={faEye} className='me-2' />
                              View
                            </Dropdown.Item>
                            {(q.status === 1 || q.status === 2) && (
                              <Dropdown.Item
                                as={Link}
                                to={`/${lng}/sales/requisitions/edit/${q.slack}`}
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className='me-2'
                                />
                                Edit
                              </Dropdown.Item>
                            )}
                            {requisitionViewPermissions?.transfer_product && q.status_data?.value_constant === "APPROVED" && (
                              <Dropdown.Item
                                onClick={() => handleTransferProduct(q.slack)}
                              >
                                <FontAwesomeIcon icon={faArrowRight} className='me-2' />
                                Transfer Product
                              </Dropdown.Item>
                            )}
                            <Dropdown.Item
                              onClick={() => {
                                setSelectedRequisitionId(q.id)
                                setShowRFQHistory(true)
                              }}
                            >
                              <FontAwesomeIcon icon={faHistory} className='me-2' />
                              RFQ Notification history
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan='12' className='text-center py-4 nosearchResult'>
                      {searchTerm
                        ? 'No requisitions found matching your search.'
                        : 'No requisitions found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className='paginationSec'>
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              siblingCount={2}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 25, 50, 100, 500, 1000]}
              showSummary={true}
              showLimit={false}
            />
          </div>
            </Tab>
            
            <Tab eventKey="products" title="Requisition Products">
              <ProductsTab
                productsRange={productsRange}
                setProductsRange={setProductsRange}
                filters={filters}
                setFilters={setFilters}
                activeTab={activeTab}
              />
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
      {showRFQHistory && (
        <RFQSendHistoryModal
          show={showRFQHistory}
          onHide={() => setShowRFQHistory(false)}
          referenceId={selectedRequisitionId}
          storeId={localStorage.getItem('store_id')}
          props={props}
        />
      )}
    </Container>
  )
}

const mapStateToProps = (globalState) => ({
  userCredentials: globalState.LoginReducer.userCredentials,
  token: globalState.LoginReducer.token,
  userEntryType: globalState.LoginReducer.userEntryType,
  currentStore: globalState.StoreReducer.currentStore,
})

export default connect(mapStateToProps)(withNamespaces()(RequisitionList))
